<template>
    <div id="login-view"
         class="full-vue-page">
        <div v-if="!waitingForBetaConfirmation && processingMessage"
             class="loading-overlay">
            <div>
                <h3>
                    {{ l(processingMessage) }}
                    <em>.</em>
                    <em>.</em>
                    <em>.</em>
                </h3>
            </div>
        </div>

        <form v-show="!waitingForBetaConfirmation && !processingMessage"
              id="login-form"
              class="no-scan-detection"
              role="form"
              @submit.prevent="login()">
            <div v-if="errorMessage"
                 class="alert alert-danger">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ errorMessage }}
            </div>

            <input id="login-form__username"
                   ref="username-input"
                   v-model="formData.username"
                   v-auto-focus
                   v-select-on-focus
                   type="text"
                   class="form-control input-lg"
                   :placeholder="usernamePlaceholder"
                   autocomplete="no-auto-fill"
                   autocorrect="off"
                   autocapitalize="off" />
            <input id="login-form__password"
                   ref="password-input"
                   v-model="formData.password"
                   v-select-on-focus
                   type="password"
                   class="form-control input-lg"
                   :placeholder="passwordPlaceholder"
                   autocomplete="no-auto-fill"
                   autocorrect="off"
                   autocapitalize="off" />

            <button class="btn btn-lg btn-primary btn-block mt-1"
                    type="submit">
                {{ l('LOGIN.SIGN_IN') }}
            </button>

            <EntraIDLoginButton v-if="entraIDLoginEnabled"
                                @error="setError"
                                @response="handleApiUserResponse" />

            <button class="btn btn-reset"
                    @click="reset">
                {{ l('RESET') }}
            </button>

            <SquareBtn v-if="showSettingsButton"
                       id="login-form__settings-btn"
                       color="dark-blue"
                       @click="openLoginSettingsModal">
                <i class="fa-solid fa-cog"></i>
            </SquareBtn>
        </form>

        <!-- src is a bound attribute so Webpack doesn't fiddle with it -->
        <img v-show="!waitingForBetaConfirmation && !processingMessage"
             id="login-form__logo"
             :src="'../img/digitickets-logo.png'"
             alt="DigiTickets" />
    </div>
</template>

<script>
import EntraIDLoginButton from '@/vue/pages/login/EntraIDLoginButton';
import LocalStorageKeys from '@/lib/Storage/LocalStorageKeys';
import LoginSettings from '@/lib/Auth/LoginSettings';
import SquareBtn from '@/vue/components/SquareBtn';
import { autoFocus } from '@/vue/directives/autoFocus';
import { container } from '@/container';
import { langFilter } from '@/vue/filters/langFilter';
import { MODAL_NAMES } from '@/vue/modals/MODAL_NAMES';
import { selectOnFocus } from '@/vue/directives/selectOnFocus';

export default {
    name: 'LoginView',

    components: {
        EntraIDLoginButton,
        SquareBtn,
    },

    directives: {
        autoFocus,
        selectOnFocus,
    },

    props: {
        /**
         * The kitchen screen is using Vue routing instead of AngularJS routing.
         * If this is true this view will use the Vue router to navigate to the next screen.
         */
        useVueRouter: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            errorMessage: null,
            processingMessage: null,
            waitingForBetaConfirmation: false,

            formData: {
                username: '',
                password: '',
            },

            /**
             * @type {boolean}
             */
            showSettingsButton: false,

            /**
             * Populated by loadSettings method, called in created or after saving settings.
             *
             * @type {LoginSettings}
             */
            settings: {},
        };
    },

    computed: {
        usernamePlaceholder() {
            return langFilter('LOGIN.USERNAME');
        },

        passwordPlaceholder() {
            return langFilter('LOGIN.PASSWORD');
        },

        entraIDLoginEnabled() {
            if (container.appConfig.entraIDLoginEnabled === true) {
                // Always shown.
                return true;
            }

            if (container.appConfig.entraIDLoginEnabled === false) {
                // Never shown.
                return false;
            }

            // Shown based on user-defined setting.
            return !!this.settings.showEntraID;
        },
    },

    async created() {
        this.loadSettings();
        this.settingsAreAvailable()
            .then((result) => {
                this.showSettingsButton = result;
            });

        // Navigate away from the login page if there is already a user.
        this.redirectIfAuthenticated();

        // Check for an error message to display from storage.
        let loginErrorMessageKey = container.userService.pullLoginErrorMessageKey();
        if (loginErrorMessageKey) {
            this.setErrorMessage(loginErrorMessageKey);
        }

        // Pre fill the username field.
        let autofillUsername = container.userService.pullAutofillLoginUsername();
        if (autofillUsername) {
            // Set the username.
            this.formData.username = autofillUsername;
            // If the username is filled skip to the password field.
            // The 250ms timeout is longer than the 100ms of the v-auto-focus directive on the username field.
            setTimeout(() => this.focusPassword(), 250);
        }
    },

    methods: {
        focusUsername() {
            if (this.$refs['username-input']) {
                this.$refs['username-input'].focus();
            }
        },

        focusPassword() {
            if (this.$refs['password-input']) {
                this.$refs['password-input'].focus();
            }
        },

        /**
         * @param {Error} error
         */
        setError(error) {
            this.setErrorMessage(null, error.message);
        },

        /**
         * @param {string|null} [langKey]
         * @param {string|null} [rawText]
         */
        setErrorMessage(langKey, rawText) {
            if (rawText) {
                this.errorMessage = rawText;
            } else if (langKey) {
                this.errorMessage = langFilter(langKey);
            } else {
                this.errorMessage = null;
            }
        },

        /**
         * @param {string|null} [langKey]
         */
        setProcessingMessage(langKey) {
            this.processingMessage = langKey || null;
        },

        login() {
            this.setErrorMessage();

            if (!this.formData.username || !this.formData.password) {
                this.setErrorMessage('LOGIN.ERROR__ENTER_DETAILS');
                return;
            }

            this.setProcessingMessage('LOGIN.PROCESSING_LOGIN');

            container.loginService
                .sendLoginRequest(this.formData)
                .then((response) => this.handleApiUserResponse(response?.data))
                .catch((error) => {
                    if (error.response && error.response.status === 401) {
                        this.setErrorMessage('LOGIN.ERROR__BAD_CREDENTIALS');
                    } else {
                        this.setErrorMessage(null, error.message);
                    }
                })
                .finally(() => {
                    this.setProcessingMessage();
                    this.$nextTick(() => {
                        this.focusUsername();
                    });
                });
        },

        redirectIfAuthenticated() {
            if (container.userService.isLoggedIn()) {
                this.setProcessingMessage('LOADING_OVERLAY');
                console.log(`User "${container.userService.username}" logged in.`);

                if (this.useVueRouter) {
                    this.$router.push('/');
                    return;
                }

                // Go back to index to continue the startup flow.
                if (container.appConfig.getCurrentScreen() === 'epos') {
                    container.navigationService.viewEposIndex();
                } else {
                    container.navigationService.goToPath('/');
                    // TODO: Need to handle vue redirection.
                }
            }
        },

        async confirmUseOfBeta() {
            return container.dialogService
                .confirm(
                    'BETA.MESSAGE',
                    {
                        cancelLabel: 'BETA.CANCEL_AND_LEAVE',
                        okLabel: 'BETA.CONTINUE',
                    },
                )
                .then((confirmed) => {
                    if (confirmed) {
                        return true;
                    }

                    throw new Error('Beta confirmation rejected.');
                });
        },

        async handleApiUserResponse(responseData) {
            // Check for a user property in the response.
            if (!responseData || !responseData.user) {
                this.setErrorMessage('LOGIN.ERROR__UNKNOWN');
                return;
            }

            // Check that user has access to at least one branch.
            if (!responseData.user.branches || responseData.user.branches.length < 1) {
                this.setErrorMessage('LOGIN.ERROR__NO_BRANCHES');
                return;
            }

            await container.loginService.loginFromApiResponse(responseData);

            // On BETA users are presented with an information notice after login.
            // They either need to click continue or logout.
            if (container.appConfig.isBeta) {
                this.waitingForBetaConfirmation = true;
                try {
                    await this.confirmUseOfBeta();
                } catch (error) {
                    container.userService.logout();
                }
                this.waitingForBetaConfirmation = false;
            }

            this.redirectIfAuthenticated();
        },

        openLoginSettingsModal() {
            container.vueModalFactory
                .open(MODAL_NAMES.LOGIN_SETTINGS)
                .then(() => {
                    this.loadSettings();
                })
                .catch(() => {
                    this.loadSettings();
                });
        },

        loadSettings() {
            this.settings = new LoginSettings(container.localStorageDataStore.find(LocalStorageKeys.LOGIN_SETTINGS) || {});
        },

        /**
         * There is currently only one login setting that can be changed - show the Entra ID login button.
         * We should only show the settings button if there is something available.
         * So determine if that setting can be changed.
         *
         * @return {Promise<boolean>}
         */
        async settingsAreAvailable() {
            if (container.appConfig.entraIDLoginEnabled === null) {
                return container.entraIDAuthService.entraIDAppExistsForCurrentEnvironment();
            }

            return false;
        },

        reset() {
            window.location.href = '/epos/reset.html';
        },
    },
};
</script>

<style lang="less">
@import "../../../less/variables";

#login-view {
    display: flex;
    flex-direction: column;
    padding: @double-gutter-width 0 0;
    position: relative;
}

#login-form {
    width: 100%;
    max-width: 350px;
    margin: 0 auto;

    .alert {
        text-align: center;
    }

    .form-control {
        &:focus {
            z-index: 2;
        }
    }

    // Make the two form inputs touch.
    #login-form__username {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom: 0;
    }

    #login-form__password {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border-top: 0;
    }

    #login-form__settings-btn {
        position: absolute;
        bottom: @gutter-width;
        right: @gutter-width;
    }
}

.btn-reset {
    position: absolute;
    left: 0;
    bottom: 0;
    background: transparent;
    color: white;
}

#login-form__logo {
    max-width: 210px;
    width: 100%;
    margin: 80px auto 0;
    display: block;
}

@media (max-width: @mobile-max) {
    #login-view {
        height: 100%;
        background: @dt-navy;
        padding: @gutter-width-mobile;
    }

    #login-form {
        max-width: none;
        flex-grow: 1;
    }

    #login-form__logo {
        margin: @double-gutter-width auto 0;
        width: 120px;
    }
}
</style>
